import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"

import { api } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { normalizarDecimal } from "@/lib/decimales"

type ClienteBreve = { id: number; codigo: string; razon_social: string; rif: string }

/** Las mismas reglas que `alcance_ordenes.normalizar_expediente`, sólo para enseñar. */
const EXPEDIENTE = /^[A-Za-z0-9][A-Za-z0-9/-]{0,19}$/

function limpiarExpediente(texto: string): string {
  return texto
    .trim()
    .replace(/^exp(?:ediente)?\.?\s*/i, "")
    .trim()
    .toUpperCase()
}

const TIPOS = [
  { valor: "LIQUIDACION", etiqueta: "Liquidación de expediente" },
  { valor: "HONORARIOS", etiqueta: "Honorarios" },
  { valor: "FINANCIAMIENTO", etiqueta: "Gasto financiado" },
  { valor: "OTRO", etiqueta: "Otro" },
] as const

/**
 * Todos los clientes activos. El servidor sirve 100 por página y la cartera del 2.0
 * pasa de cien: con una sola página, el cliente 101 no saldría en la lista.
 */
export async function todosLosClientes(): Promise<ClienteBreve[]> {
  const todos: ClienteBreve[] = []
  for (let desde = 0; desde < 3000; desde += 100) {
    const pagina = await api.get<ClienteBreve[]>(
      `/api/v1/clientes?limit=100&offset=${desde}&activo=true`,
    )
    todos.push(...pagina)
    if (pagina.length < 100) break
  }
  return todos.sort((a, b) => a.razon_social.localeCompare(b.razon_social, "es"))
}

/**
 * Crear una cuenta por cobrar A MANO (15/10).
 *
 * ⚠ **Es el puente hasta que haya motor operativo**: hoy ningún expediente genera sus
 * CxC, y sin una no hay a qué aplicar un ingreso —queda todo de anticipo—. Se crea
 * aquí y el ingreso se cierra desde Tesorería → Ingresos → «Aplicar anticipo».
 *
 * ⚠ **El expediente se declara como en una orden de pago**: casilla y número del
 * 2.0; el servidor lo pone delante del concepto —«EXP 2026143 · …»— y lo guarda en su
 * columna. No es `expediente_id`: el expediente del 3.0 todavía no existe.
 */
export function ModalNuevaCxc({ onCerrar }: { onCerrar: () => void }) {
  const clienteQuery = useQueryClient()
  const [clienteId, setClienteId] = useState<number | null>(null)
  const [vinculada, setVinculada] = useState(false)
  const [expediente, setExpediente] = useState("")
  const [concepto, setConcepto] = useState("")
  const [monto, setMonto] = useState("")
  const [moneda, setMoneda] = useState<"USD" | "BS">("BS")
  const [tipo, setTipo] = useState<(typeof TIPOS)[number]["valor"]>("LIQUIDACION")

  const clientes = useQuery({ queryKey: ["clientes", "todos-activos"], queryFn: todosLosClientes })

  const numero = limpiarExpediente(expediente)
  const expedienteValido = !vinculada || EXPEDIENTE.test(numero)
  const prefijo = vinculada && numero !== "" ? `EXP ${numero} · ` : ""
  const montoNormal = normalizarDecimal(monto)
  const valido =
    clienteId !== null &&
    expedienteValido &&
    concepto.trim().length >= 3 &&
    montoNormal !== null &&
    Number(montoNormal) > 0

  const crear = useMutation({
    meta: { exito: "Cuenta por cobrar creada" },
    mutationFn: () =>
      api.post("/api/v1/cxc", {
        cliente_id: clienteId,
        tipo,
        concepto: concepto.trim(),
        monto: montoNormal,
        moneda,
        expediente_numero: vinculada ? expediente.trim() : null,
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["cxc"] })
      onCerrar()
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (valido) crear.mutate()
  }

  return (
    <Modal abierto titulo="Nueva cuenta por cobrar" onCerrar={onCerrar}>
      <form className="space-y-4" onSubmit={enviar}>
        <div>
          <label htmlFor="cxc-cliente" className="mb-1 block text-xs font-medium uppercase">
            Cliente
          </label>
          <SelectorBuscable
            id="cxc-cliente"
            opciones={(clientes.data ?? []).map((c) => ({
              valor: c.id,
              etiqueta: c.razon_social,
              detalle: c.rif,
            }))}
            valor={clienteId}
            onCambiar={setClienteId}
            placeholder={clientes.isPending ? "Cargando clientes…" : "Nombre o RIF…"}
          />
        </div>

        {/* La casilla de las órdenes de pago, con el mismo motivo: que se DECLARE. */}
        <div className="space-y-2">
          <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0">
            <input
              type="checkbox"
              checked={vinculada}
              onChange={(e) => setVinculada(e.target.checked)}
              className="size-4"
            />
            Vinculada a un expediente
          </label>
          {vinculada && (
            <div>
              <label htmlFor="cxc-expediente" className="mb-1 block text-xs font-medium uppercase">
                N.º de expediente
              </label>
              <input
                id="cxc-expediente"
                value={expediente}
                onChange={(e) => setExpediente(e.target.value)}
                placeholder="2026143"
                maxLength={40}
                required
                // biome-ignore lint/a11y/noAutofocus: aparece al marcar la casilla; el foco va a lo que se acaba de pedir
                autoFocus
                aria-invalid={expediente.trim() !== "" && !expedienteValido}
                className="campo"
              />
              {expediente.trim() !== "" && !expedienteValido && (
                <p className="mt-1 text-xs" style={{ color: "var(--destructive)" }}>
                  Hasta 20 caracteres: letras, dígitos, guion o barra.
                </p>
              )}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="cxc-concepto" className="mb-1 block text-xs font-medium uppercase">
            Concepto
          </label>
          <input
            id="cxc-concepto"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            maxLength={180}
            placeholder="Liquidación del despacho"
            className="campo"
          />
          {prefijo !== "" && concepto.trim() !== "" && (
            <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
              Se guardará como «{prefijo}
              {concepto.trim()}».
            </p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <div>
            <label htmlFor="cxc-monto" className="mb-1 block text-xs font-medium uppercase">
              Monto
            </label>
            <input
              id="cxc-monto"
              inputMode="decimal"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="1.300.314,40"
              aria-invalid={monto.trim() !== "" && montoNormal === null}
              className="campo tabular-nums"
            />
          </div>
          <div>
            <label htmlFor="cxc-moneda" className="mb-1 block text-xs font-medium uppercase">
              Moneda
            </label>
            <select
              id="cxc-moneda"
              value={moneda}
              onChange={(e) => setMoneda(e.target.value as "USD" | "BS")}
              className="campo"
            >
              <option value="BS">BS</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="cxc-tipo" className="mb-1 block text-xs font-medium uppercase">
            Tipo
          </label>
          <select
            id="cxc-tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as typeof tipo)}
            className="campo"
          >
            {TIPOS.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.etiqueta}
              </option>
            ))}
          </select>
        </div>

        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Para cerrarla con el dinero que ya entró, aplica el ingreso desde Tesorería → Ingresos →
          «Aplicar anticipo».
        </p>

        {crear.isError && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {crear.error instanceof Error ? crear.error.message : "No se pudo crear."}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Boton type="button" variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="primario" disabled={!valido || crear.isPending}>
            {crear.isPending ? "Creando…" : "Crear cuenta por cobrar"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
